import React, { useState, useEffect } from 'react';
import { settingsApi, authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  Calendar,
  Mail,
  Zap,
  Users,
  Shield,
  AlertTriangle,
  Save,
  Power,
  Link,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  CreditCard,
  Phone
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const [automationConfig, setAutomationConfig] = useState({
    calendly_link: '',
    max_nudges: 2,
    global_frequency_cap_hours: 24,
    enable_scheduling_agent: true,
    enable_follow_ups: true,
    enable_proposal_generation: true,
    automation_mode: 'manual',
    daily_credit_cap: 100,
    max_credits_per_lead_per_day: 15
  });
  const [killSwitches, setKillSwitches] = useState({
    all_ai_disabled: false,
    scheduling_disabled: false,
    follow_ups_disabled: false,
    proposals_disabled: false,
    emails_disabled: false
  });
  const [users, setUsers] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [configRes, switchesRes, usersRes, integrationsRes, schedulerRes] = await Promise.all([
        settingsApi.getAutomation(),
        settingsApi.getKillSwitches().catch(() => ({ data: {} })),
        authApi.listUsers().catch(() => ({ data: [] })),
        settingsApi.getIntegrations().catch(() => ({ data: null })),
        settingsApi.getSchedulerStatus().catch(() => ({ data: null }))
      ]);
      setAutomationConfig(configRes.data);
      setKillSwitches(switchesRes.data);
      setUsers(usersRes.data);
      setIntegrations(integrationsRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutomation = async () => {
    setSaving(true);
    try {
      await settingsApi.updateAutomation(automationConfig);
      toast.success('Automation settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKillSwitches = async () => {
    setSaving(true);
    try {
      await settingsApi.updateKillSwitches(killSwitches);
      toast.success('Kill switches updated');
    } catch (error) {
      toast.error('Failed to update kill switches');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await authApi.updateRole(userId, newRole);
      toast.success('User role updated');
      loadSettings();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleSaveCalendlyConfig = async () => {
    setSaving(true);
    try {
      await settingsApi.updateCalendlyIntegration({
        calendly_link: automationConfig.calendly_link
      });
      toast.success('Calendly configuration saved');
    } catch (error) {
      toast.error('Failed to save Calendly config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const IntegrationStatus = ({ configured, label }) => (
    <div className={`flex items-center gap-2 ${configured ? 'text-emerald-600' : 'text-slate-400'}`}>
      {configured ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      <span className="text-sm">{label}</span>
    </div>
  );

  return (
    <div className="p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Configure automation, integrations, and manage users</p>
      </div>

      <Tabs defaultValue="automation" className="space-y-6">
        <TabsList>
          <TabsTrigger value="automation" data-testid="tab-automation">
            <Zap className="w-4 h-4 mr-2" />
            Automation
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Link className="w-4 h-4 mr-2" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          {isSuperAdmin() && (
            <TabsTrigger value="kill-switches" data-testid="tab-kill-switches">
              <Power className="w-4 h-4 mr-2" />
              Kill Switches
            </TabsTrigger>
          )}
        </TabsList>

        {/* Automation Settings */}
        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <CardTitle>Automation Configuration</CardTitle>
              <CardDescription>
                Configure AI scheduling, follow-ups, and automation rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* CRITICAL: Automation Mode Control */}
              <div className="p-4 border-2 border-blue-200 bg-blue-50 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Automation Mode</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setAutomationConfig({...automationConfig, automation_mode: 'manual'})}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      automationConfig.automation_mode === 'manual'
                        ? 'border-blue-500 bg-white shadow-md'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                    data-testid="mode-manual"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className={`w-5 h-5 ${automationConfig.automation_mode === 'manual' ? 'text-blue-500' : 'text-slate-400'}`} />
                      <span className="font-semibold">Manual Mode</span>
                      {automationConfig.automation_mode === 'manual' && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 text-left">
                      Actions are queued for your approval before sending. Recommended for safety.
                    </p>
                  </button>
                  <button
                    onClick={() => setAutomationConfig({...automationConfig, automation_mode: 'auto'})}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      automationConfig.automation_mode === 'auto'
                        ? 'border-amber-500 bg-white shadow-md'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                    data-testid="mode-auto"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className={`w-5 h-5 ${automationConfig.automation_mode === 'auto' ? 'text-amber-500' : 'text-slate-400'}`} />
                      <span className="font-semibold">Auto Mode</span>
                      {automationConfig.automation_mode === 'auto' && (
                        <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 text-left">
                      Actions are executed automatically (subject to caps). Use with caution.
                    </p>
                  </button>
                </div>
              </div>

              {/* Credit Caps */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold">Credit Safety Caps</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Daily Credit Cap</Label>
                    <Input
                      type="number"
                      value={automationConfig.daily_credit_cap || 100}
                      onChange={(e) => setAutomationConfig({
                        ...automationConfig,
                        daily_credit_cap: parseFloat(e.target.value) || 100
                      })}
                      min="0"
                      data-testid="daily-cap-input"
                    />
                    <p className="text-xs text-slate-400">Maximum credits to spend per day across all leads</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Credits Per Lead/Day</Label>
                    <Input
                      type="number"
                      value={automationConfig.max_credits_per_lead_per_day || 15}
                      onChange={(e) => setAutomationConfig({
                        ...automationConfig,
                        max_credits_per_lead_per_day: parseFloat(e.target.value) || 15
                      })}
                      min="0"
                      data-testid="per-lead-cap-input"
                    />
                    <p className="text-xs text-slate-400">Maximum credits per individual lead per day</p>
                  </div>
                </div>
              </div>

              {/* Calendly Link */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Calendly Booking Link
                </Label>
                <Input
                  value={automationConfig.calendly_link}
                  onChange={(e) => setAutomationConfig({
                    ...automationConfig,
                    calendly_link: e.target.value
                  })}
                  placeholder="https://calendly.com/your-link"
                  data-testid="calendly-link-input"
                />
                <p className="text-xs text-slate-400">
                  This link will be included in all AI-generated booking messages
                </p>
              </div>

              {/* Nudge Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Nudges per Lead</Label>
                  <Select
                    value={automationConfig.max_nudges?.toString() || "2"}
                    onValueChange={(v) => setAutomationConfig({
                      ...automationConfig,
                      max_nudges: parseInt(v)
                    })}
                  >
                    <SelectTrigger data-testid="max-nudges-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frequency Cap (hours)</Label>
                  <Select
                    value={automationConfig.global_frequency_cap_hours?.toString() || "24"}
                    onValueChange={(v) => setAutomationConfig({
                      ...automationConfig,
                      global_frequency_cap_hours: parseInt(v)
                    })}
                  >
                    <SelectTrigger data-testid="frequency-cap-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[12, 24, 48, 72].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} hours</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <h4 className="font-medium">Feature Toggles</h4>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">AI Scheduling Agent</p>
                    <p className="text-sm text-slate-500">Automated booking messages</p>
                  </div>
                  <Switch
                    checked={automationConfig.enable_scheduling_agent}
                    onCheckedChange={(checked) => setAutomationConfig({
                      ...automationConfig,
                      enable_scheduling_agent: checked
                    })}
                    data-testid="toggle-scheduling"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Follow-up Automation</p>
                    <p className="text-sm text-slate-500">Automatic reminders and confirmations (24h, 1h, Day 3/7/14)</p>
                  </div>
                  <Switch
                    checked={automationConfig.enable_follow_ups}
                    onCheckedChange={(checked) => setAutomationConfig({
                      ...automationConfig,
                      enable_follow_ups: checked
                    })}
                    data-testid="toggle-followups"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Proposal Generation</p>
                    <p className="text-sm text-slate-500">AI-generated proposals</p>
                  </div>
                  <Switch
                    checked={automationConfig.enable_proposal_generation}
                    onCheckedChange={(checked) => setAutomationConfig({
                      ...automationConfig,
                      enable_proposal_generation: checked
                    })}
                    data-testid="toggle-proposals"
                  />
                </div>
              </div>

              <Button
                className="btn-primary gap-2"
                onClick={handleSaveAutomation}
                disabled={saving}
                data-testid="save-automation-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Email (Resend)
                </CardTitle>
                <CardDescription>
                  Transactional email delivery via Resend
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntegrationStatus 
                  configured={integrations?.email?.configured} 
                  label={integrations?.email?.configured ? 'Connected' : 'Not configured'} 
                />
                
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Provider</span>
                    <span className="font-medium">{integrations?.email?.provider || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">From Email</span>
                    <span className="font-medium">{integrations?.email?.from_email || 'N/A'}</span>
                  </div>
                </div>

                <p className="text-xs text-slate-400">
                  Email configuration is managed via environment variables (RESEND_API_KEY, EMAIL_FROM)
                </p>
              </CardContent>
            </Card>

            {/* LLM Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  AI / LLM (OpenAI)
                </CardTitle>
                <CardDescription>
                  GPT-5.2 for message generation and analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntegrationStatus 
                  configured={integrations?.llm?.configured} 
                  label={integrations?.llm?.configured ? 'Connected' : 'API key not set'} 
                />
                
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Provider</span>
                    <span className="font-medium">{integrations?.llm?.provider || 'OpenAI'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Model</span>
                    <span className="font-medium">{integrations?.llm?.model || 'gpt-5.2'}</span>
                  </div>
                </div>

                <p className="text-xs text-slate-400">
                  Set OPENAI_API_KEY in environment to enable AI features
                </p>
              </CardContent>
            </Card>

            {/* Calendly Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Calendly
                </CardTitle>
                <CardDescription>
                  Booking link for discovery calls
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntegrationStatus 
                  configured={!!automationConfig.calendly_link} 
                  label={automationConfig.calendly_link ? 'Link configured' : 'No link set'} 
                />
                
                <div className="space-y-2">
                  <Label>Calendly Booking Link</Label>
                  <Input
                    value={automationConfig.calendly_link || ''}
                    onChange={(e) => setAutomationConfig({
                      ...automationConfig,
                      calendly_link: e.target.value
                    })}
                    placeholder="https://calendly.com/your-link/discovery"
                    data-testid="calendly-link-integration"
                  />
                </div>

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleSaveCalendlyConfig}
                  disabled={saving}
                  data-testid="save-calendly-btn"
                >
                  <Save className="w-4 h-4" />
                  Save Calendly Link
                </Button>

                <p className="text-xs text-slate-400">
                  For full Calendly API integration (webhooks, availability), add CALENDLY_API_KEY to environment
                </p>
              </CardContent>
            </Card>

            {/* SMS / Twilio Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-teal-600" />
                  SMS & Voice (Twilio)
                </CardTitle>
                <CardDescription>
                  SMS messaging and voice calls for lead engagement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntegrationStatus 
                  configured={integrations?.sms?.configured} 
                  label={integrations?.sms?.configured ? 'Connected' : 'Not configured'} 
                />
                
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Account</span>
                    <span className="font-medium">{integrations?.sms?.account_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Phone Numbers</span>
                    <span className="font-medium">{integrations?.sms?.phone_numbers?.length || 0} available</span>
                  </div>
                  {integrations?.sms?.default_from && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Default From</span>
                      <span className="font-medium">{integrations?.sms?.default_from}</span>
                    </div>
                  )}
                </div>

                {integrations?.sms?.phone_numbers?.length > 0 && (
                  <div className="text-xs text-slate-400 space-y-1">
                    <p className="font-medium">Available numbers:</p>
                    {integrations.sms.phone_numbers.map((num, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span>{num.phone_number}</span>
                        {num.capabilities?.sms && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">SMS</span>}
                        {num.capabilities?.voice && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Voice</span>}
                      </div>
                    ))}
                  </div>
                )}

                {!integrations?.sms?.configured && (
                  <p className="text-xs text-slate-400">
                    Add TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN to environment. Purchase a phone number in Twilio Console to send SMS.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Scheduler Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Follow-up Scheduler
                </CardTitle>
                <CardDescription>
                  Automated follow-up processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntegrationStatus 
                  configured={schedulerStatus?.scheduler_running} 
                  label={schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'} 
                />
                
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Check Interval</span>
                    <span className="font-medium">{schedulerStatus?.check_interval_seconds || 60}s</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Pending Reminders</span>
                    <span className="font-medium">{schedulerStatus?.pending_reminders || 0}</span>
                  </div>
                </div>

                {/* LLM Token Usage Warning */}
                {integrations?.scheduler?.uses_llm_tokens && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Uses LLM Tokens</p>
                        <p className="text-amber-700">
                          Each automated follow-up costs {integrations?.scheduler?.follow_up_costs?.per_message || 3} credits
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={loadSettings}
                  data-testid="refresh-scheduler-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Status
                </Button>

                <div className="text-xs text-slate-400">
                  <p className="font-medium mb-1">Automatic follow-ups:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>24h reminder before booked calls</li>
                    <li>1h reminder before booked calls</li>
                    <li>Day 3, 7, 14 proposal follow-ups</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
              <CardDescription>
                Manage team members and their roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((u) => (
                  <div 
                    key={u.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="font-medium text-slate-600">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {isSuperAdmin() && u.id !== user.id ? (
                        <Select
                          value={u.role}
                          onValueChange={(v) => handleUpdateUserRole(u.id, v)}
                        >
                          <SelectTrigger className="w-40" data-testid={`role-select-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sales_user">Sales User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          u.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                          u.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {u.role.replace('_', ' ').toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kill Switches Tab */}
        {isSuperAdmin() && (
          <TabsContent value="kill-switches">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Emergency Kill Switches
                </CardTitle>
                <CardDescription>
                  Immediately disable AI features in case of emergency
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-red-800">DISABLE ALL AI</p>
                      <p className="text-sm text-red-600">
                        Emergency stop for all AI-powered features
                      </p>
                    </div>
                    <Switch
                      checked={killSwitches.all_ai_disabled}
                      onCheckedChange={(checked) => setKillSwitches({
                        ...killSwitches,
                        all_ai_disabled: checked
                      })}
                      data-testid="kill-all-ai"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'scheduling_disabled', label: 'Scheduling Agent' },
                    { key: 'follow_ups_disabled', label: 'Follow-ups' },
                    { key: 'proposals_disabled', label: 'Proposal Generation' },
                    { key: 'emails_disabled', label: 'Email Sending' }
                  ].map(({ key, label }) => (
                    <div 
                      key={key}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <span className="font-medium">{label}</span>
                      <Switch
                        checked={killSwitches[key]}
                        onCheckedChange={(checked) => setKillSwitches({
                          ...killSwitches,
                          [key]: checked
                        })}
                        data-testid={`kill-${key}`}
                      />
                    </div>
                  ))}
                </div>

                <Button
                  variant="destructive"
                  className="gap-2"
                  onClick={handleSaveKillSwitches}
                  disabled={saving}
                  data-testid="save-kill-switches"
                >
                  <Power className="w-4 h-4" />
                  {saving ? 'Updating...' : 'Update Kill Switches'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
